import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Building2, MessageCircle, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useMapsConfig } from "@/lib/maps";
import PushNotificationsCard from "@/components/PushNotificationsCard";
import AdminPushSimulator from "@/components/AdminPushSimulator";

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const maps = useMapsConfig();

  useEffect(() => {
    api.get("/admin/settings")
      .then(({ data }) => setForm(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;
  if (!form) return <div className="card-soft p-10 text-center">Impostazioni non disponibili. <button className="btn-secondary" onClick={() => window.location.reload()}>Riprova</button></div>;

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", {
        company_name: form.company_name,
        company_address: form.company_address,
        company_city: form.company_city,
        company_country: form.company_country,
        company_vat: form.company_vat,
        company_email: form.company_email,
        sales_email: form.sales_email,
        lead_notifications_enabled: !!form.lead_notifications_enabled,
      });
      setForm(data);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <p className="overline">Configurazione</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Impostazioni</h1>
      </div>

      <section className="card-soft p-6 sm:p-7 space-y-5">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><Building2 className="h-5 w-5" /></span>
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight">Dati azienda (per fatturazione)</h2>
            <p className="text-sm text-dapnow-muted">Vengono mostrati su tutte le fatture PDF generate.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Ragione sociale" testid="settings-company-name" v={form.company_name || ""} on={set("company_name")} />
          <Field label="Email aziendale" testid="settings-company-email" v={form.company_email || ""} on={set("company_email")} />
          <Field label="Indirizzo" testid="settings-company-address" v={form.company_address || ""} on={set("company_address")} />
          <Field label="Città / NPA" testid="settings-company-city" v={form.company_city || ""} on={set("company_city")} />
          <Field label="Paese" testid="settings-company-country" v={form.company_country || ""} on={set("company_country")} />
          <Field label="Partita IVA" testid="settings-company-vat" v={form.company_vat || ""} on={set("company_vat")} />
        </div>

        <div className="flex justify-end pt-2">
          <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="btn-primary">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Salvataggio…" : "Salva impostazioni"}
          </button>
        </div>
      </section>

      <section className="card-soft p-6 sm:p-7 space-y-5">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><Building2 className="h-5 w-5" /></span>
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight">Lead B2B & Notifiche</h2>
            <p className="text-sm text-dapnow-muted">Indirizzo email che riceve le richieste demo dal form B2B.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <Field label="Sales email" testid="settings-sales-email" v={form.sales_email || ""} on={set("sales_email")} />
          <label className="flex items-center gap-3 cursor-pointer select-none rounded-xl border border-dapnow-line p-3 bg-white">
            <input
              type="checkbox"
              data-testid="settings-lead-notif-toggle"
              checked={!!form.lead_notifications_enabled}
              onChange={(e) => setForm((s) => ({ ...s, lead_notifications_enabled: e.target.checked }))}
              className="h-4 w-4 accent-dapnow-blue"
            />
            <div>
              <p className="text-sm font-semibold tracking-tight">Notifiche email lead attive</p>
              <p className="text-xs text-dapnow-muted">Invia notifica a sales email quando arriva una nuova richiesta demo.</p>
            </div>
          </label>
        </div>
        <p className="text-xs text-dapnow-muted">Resend non è ancora integrato — al momento le notifiche sono loggate sul server.</p>

        <div className="flex justify-end pt-2">
          <button onClick={save} disabled={saving} data-testid="settings-save-leads-btn" className="btn-primary">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Salvataggio…" : "Salva"}
          </button>
        </div>
      </section>

      <WhatsAppSection />

      <section className="card-soft p-6 sm:p-7">
        <h2 className="font-display text-lg font-bold tracking-tight">Integrazioni</h2>
        <ul className="mt-4 divide-y divide-dapnow-line">
          <IntegrationRow
            name="Google Maps"
            desc="Autocomplete indirizzi, routing e tracking GPS"
            status={maps.enabled ? "active" : "pending"}
            note={maps.enabled ? "Configurato — provider: Google" : "Inserisci GOOGLE_MAPS_API_KEY in backend/.env e REACT_APP_GOOGLE_MAPS_API_KEY nel frontend"}
          />
          <IntegrationRow name="Stripe" desc="Pagamenti carta + split driver/piattaforma" status="planned" note="Fase 3 — pagamenti attualmente simulati" />
          <IntegrationRow name="Twilio WhatsApp" desc="Notifiche stato consegna · vedi sezione dedicata sopra" status="pending" note="Configurazione e stato effettivo nella sezione WhatsApp" />
          <IntegrationRow name="Resend Email" desc="Email transactional + ricevute" status="active" note="Integrato — fallback su log se RESEND_API_KEY mancante" />
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-bold tracking-tight mb-3">Notifiche push admin</h2>
        <PushNotificationsCard />
      </section>

      <section>
        <h2 className="text-lg font-bold tracking-tight mb-3">Test push reali</h2>
        <AdminPushSimulator />
      </section>
    </div>
  );
}


function WhatsAppSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/whatsapp/status");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!testTo) {
      toast.error("Inserisci un numero E.164 (+41 …)");
      return;
    }
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/whatsapp/test", { to: testTo });
      const status = res.result?.status;
      if (["accepted", "queued", "sending", "sent", "delivered", "read"].includes(status)) toast.success(`Stato provider: ${status}. La consegna si verifica nei log.`);
      else if (status === "mocked") toast.info(`Mocked: ${res.result.reason || "credenziali assenti / flag off"}`);
      else if (status === "failed") toast.error(`Invio fallito: ${res.result.error}`);
      else toast.warning(`Skipped: ${res.result.reason || res.result.error || "Da verificare"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <section className="card-soft p-6 text-center text-dapnow-muted">Caricamento WhatsApp…</section>;
  }

  if (!data) return <section className="card-soft p-6">Stato WhatsApp non disponibile. <button className="btn-secondary" onClick={load}>Riprova</button></section>;

  const cfg = data.config;
  const totalSent = (data.counters?.sent || 0);
  const totalMocked = (data.counters?.mocked || 0);
  const totalFailed = (data.counters?.failed || 0);
  const totalSkipped = (data.counters?.skipped || 0);

  return (
    <section className="card-soft p-6 sm:p-7 space-y-5" data-testid="admin-whatsapp-section">
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${cfg.ready_for_sending ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          <MessageCircle className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-lg font-bold tracking-tight">WhatsApp Notifications</h2>
            <span
              className={`pill text-[10px] ${
                cfg.integration_status === "ready_to_test" ? "bg-emerald-50 text-emerald-700"
                : cfg.integration_status === "incomplete" ? "bg-amber-50 text-amber-700"
                : "bg-zinc-100 text-dapnow-muted"
              }`}
              data-testid="wa-integration-status-pill"
            >
              {cfg.integration_status === "ready_to_test" ? "Configurato · consegna da verificare"
                : cfg.integration_status === "incomplete" ? "Configurazione incompleta"
                : "Disabilitato"}
            </span>
            {cfg.fallback_log && (
              <span className="pill text-[10px] bg-blue-50 text-dapnow-blue" data-testid="wa-fallback-log-pill">
                Fallback log attivo
              </span>
            )}
          </div>
          <p className="text-sm text-dapnow-muted">
            Notifiche di spedizione via Twilio, solo con consenso e template approvati. La configurazione locale non certifica l’approvazione di Meta o la consegna dei messaggi.
          </p>
        </div>
      </div>

      {/* Config grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
        <ConfigCard label="WhatsApp abilitato" value={cfg.enabled ? "Sì" : "No"} ok={cfg.enabled} />
        <ConfigCard label="Twilio configurato" value={cfg.credentials_configured ? "Sì" : "No"} ok={cfg.credentials_configured} />
        <ConfigCard label="Sender" value={cfg.sender || "—"} mono />
        <ConfigCard label="Admin recipient" value={cfg.admin_recipient || "—"} mono />
      </div>

      {cfg.missing_requirements?.length > 0 && <p className="text-xs text-amber-800 break-words">Requisiti mancanti: {cfg.missing_requirements.join(", ")}</p>}

      {/* Counters */}
      <div className="flex flex-wrap gap-2 text-xs">
        <Counter label="In coda" value={(data.counters?.queued || 0) + (data.counters?.accepted || 0) + (data.counters?.pending || 0) + (data.counters?.sending || 0)} tone="amber" />
        <Counter label="Inviati al canale" value={totalSent} tone="muted" />
        <Counter label="Consegnati" value={data.counters?.delivered || 0} tone="emerald" />
        <Counter label="Letti" value={data.counters?.read || 0} tone="emerald" />
        <Counter label="Non consegnati" value={data.counters?.undelivered || 0} tone="red" />
        <Counter label="Da riconciliare" value={data.counters?.unknown || 0} tone="amber" />
        <Counter label="Mocked" value={totalMocked} tone="amber" />
        <Counter label="Falliti" value={totalFailed} tone="red" />
        <Counter label="Saltati" value={totalSkipped} tone="muted" />
      </div>

      {/* Test sender */}
      <div className="rounded-2xl border border-dapnow-line bg-dapnow-bg/40 p-4">
        <p className="text-xs font-bold text-dapnow-muted uppercase tracking-wider">Test invio</p>
        <div className="mt-2 grid sm:grid-cols-[1fr_auto] gap-2 items-end">
          <Field label="Destinatario (E.164)" testid="wa-test-to" v={testTo} on={(e) => setTestTo(e.target.value)} />
          <button onClick={sendTest} disabled={sending} data-testid="wa-test-send" className="btn-primary h-11">
            <Send className="h-4 w-4 mr-1.5" /> {sending ? "Invio…" : "Invia"}
          </button>
        </div>
        <p className="text-[11px] text-dapnow-muted mt-2">
          A servizio disabilitato il test è solo simulato. L’invio reale richiede configurazione completa, template di test approvato e destinatario autorizzato per il collaudo; usa esclusivamente un numero di cui hai il consenso.
        </p>
      </div>

      {/* Last events */}
      <div>
        <p className="text-xs font-bold text-dapnow-muted uppercase tracking-wider mb-2">Ultimi 5 eventi</p>
        {data.last_events.length === 0 ? (
          <p className="text-sm text-dapnow-muted italic">Nessun evento ancora registrato.</p>
        ) : (
          <ul className="divide-y divide-dapnow-line border border-dapnow-line rounded-xl bg-white">
            {data.last_events.map((ev, i) => (
              <li key={i} data-testid={`wa-event-${i}`} className="py-2.5 px-4 flex flex-wrap items-center gap-3 text-xs break-all">
                <span className={`pill text-[10px] ${EVENT_TONE[ev.status] || "bg-dapnow-bg text-dapnow-muted"}`}>
                  {ev.status}
                </span>
                <span className="font-mono text-dapnow-muted">{(ev.created_at || "").slice(0, 19).replace("T", " ")}</span>
                <span className="font-semibold tracking-tight">{ev.event_type}</span>
                <span className="text-dapnow-muted">→</span>
                <span className="font-mono">{ev.recipient}</span>
                {ev.error && <span className="ml-auto text-red-600 text-[11px] truncate max-w-[40%]" title={ev.error}>{ev.error}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap gap-2 text-[11px] text-dapnow-muted">
        <span className="font-semibold uppercase">Eventi predisposti:</span>
        {data.available_event_types.map((t) => (
          <span key={t} className="pill bg-dapnow-bg text-dapnow-ink">{t}</span>
        ))}
      </div>
    </section>
  );
}

const EVENT_TONE = {
  sent: "bg-emerald-50 text-emerald-700",
  mocked: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-700",
  skipped: "bg-dapnow-bg text-dapnow-muted",
};

function ConfigCard({ label, value, mono = false, ok }) {
  return (
    <div className="rounded-xl border border-dapnow-line bg-white p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-dapnow-muted">{label}</p>
      <p className={`text-sm mt-1 truncate ${mono ? "font-mono" : "font-semibold"} ${ok === false ? "text-amber-700" : ok === true ? "text-emerald-700" : "text-dapnow-ink"}`}>{value}</p>
    </div>
  );
}

function Counter({ label, value, tone }) {
  const cls = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber:   "bg-amber-50 text-amber-700",
    red:     "bg-red-50 text-red-700",
    muted:   "bg-dapnow-bg text-dapnow-muted",
  }[tone];
  return (
    <span className={`pill ${cls}`} data-testid={`wa-counter-${label.toLowerCase()}`}>{label}: <strong className="ml-1">{value}</strong></span>
  );
}

function Field({ label, v, on, testid }) {
  return (
    <div>
      <label className="text-sm font-semibold tracking-tight">{label}</label>
      <input data-testid={testid} value={v} onChange={on} className="input-tall mt-1.5 w-full" />
    </div>
  );
}

function IntegrationRow({ name, desc, status, note }) {
  const tone = {
    active: "bg-emerald-50 text-emerald-700",
    pending: "bg-amber-50 text-amber-700",
    planned: "bg-dapnow-bg text-dapnow-muted",
  }[status];
  const label = { active: "Attiva", pending: "Predisposta", planned: "Pianificata" }[status];
  return (
    <li className="py-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="font-display text-base font-semibold tracking-tight">{name}</p>
        <p className="text-xs text-dapnow-muted">{desc}</p>
        <p className="text-xs text-dapnow-muted mt-1 italic">{note}</p>
      </div>
      <span className={`pill ${tone}`}>{label}</span>
    </li>
  );
}
